import { useEffect, useMemo, useState, useRef } from "react";
import {
  ActionIcon,
  Center,
  Flex,
  Group,
  Loader,
  TextInput,
  Title,
  Tooltip,
  useMantineTheme,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import {
  MantineReactTable,
  type MRT_ColumnDef,
  type MRT_SortingState,
  type MRT_ColumnFiltersState,
} from "mantine-react-table";
import { IconEye, IconLink, IconSearch } from "@tabler/icons-react";
import { useNavigate } from "react-router-dom";
import trpc from "@/shared/utils/trpc";
import { handleTrpcError } from "@/shared/utils/trpc/handletrpcError.util";
import { useDebouncedValue } from "@mantine/hooks";
import VerificationStatusBadge from "../AdminStausBadge";
import {
  getSubIndustryLabel,
  getIndustryLabel,
  getCollaborationIntentLabel,
  OPERATING_INDUSTRY_LABELS,
  SUB_INDUSTRY_LABELS,
  COLLABORATION_INTENT_LABELS,
  COUNTRIES_FILTER_DATA,
  REGIONS_FILTER_DATA,
  regionsData,
} from "@/components/shared/constants/general.const";
import { displayHeadquartersText } from "@/shared/constants/signup.const";
import { buildCompanyDiscoverUrl, copyTextToClipboard } from "@/shared/utils/discoverUrl.util";

const VERIFICATION_STATUS_LABELS: Record<string, string> = {
  DRAFT: "Draft",
  UNDER_REVIEW: "Under Verification",
  VERIFIED: "Verified",
  ACTION_REQUIRED: "Rejected",
  SYSTEM_VERIFIED: "System Verified",
};

type CompanyListItem = {
  id: string;
  legalName: string | null;
  gtid: string;
  status: string;
  industryCategory: string[] | null;
  subIndustry: string[];
  collaborationIntent: string[];
  operatingCountries: string[];
  preferredCollaborationRegions: string[];
  lastSeenAt: string | null;
  viewCount: number;
  creditBalance: number;
};

const PAGE_SIZE = 10;

async function copyDiscoverUrl(companyId: string) {
  const url = buildCompanyDiscoverUrl(companyId);
  try {
    await copyTextToClipboard(url);
    notifications.show({
      title: "Link copied",
      message: url,
      color: "green",
    });
  } catch {
    notifications.show({
      title: "Could not copy link",
      message: "Please copy the discover URL manually.",
      color: "red",
    });
  }
}

export default function AdminCompaniesPage() {
  const navigate = useNavigate();
  const theme = useMantineTheme();

  const [companies, setCompanies] = useState<CompanyListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [totalRows, setTotalRows] = useState(0);
  const [columnVisibility, setColumnVisibility] = useState<Record<string, boolean>>({
    industryCategory: false,
    subIndustry: false,
    collaborationIntent: false,
    operatingCountries: false,
    preferredCollaborationRegions: false,
  });

  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: PAGE_SIZE,
  });
  const [columnFilters, setColumnFilters] = useState<MRT_ColumnFiltersState>([]);
  const [sorting, setSorting] = useState<MRT_SortingState>([]);
  const [referenceIdSearch, setReferenceIdSearch] = useState("");
  const [debouncedFilters] = useDebouncedValue(columnFilters, 400);
  const [debouncedReferenceIdSearch] = useDebouncedValue(referenceIdSearch, 400);

  const previousParamsRef = useRef<string>("");

  const filterParams = useMemo(() => {
    const filtered = debouncedFilters.filter((f) => f.value !== undefined && f.value !== null && f.value !== "");
    return Object.fromEntries(filtered.map((f) => [f.id, f.value]));
  }, [debouncedFilters]);

  useEffect(() => {
    const params = {
      page: pagination.pageIndex + 1,
      limit: pagination.pageSize,
      sorting: sorting.map((s) => ({ id: s.id, desc: s.desc })),
    };

    const paramsString = JSON.stringify({ ...params, filterParams, referenceId: debouncedReferenceIdSearch });

    if (paramsString === previousParamsRef.current) {
      return;
    }

    previousParamsRef.current = paramsString;

    const fetchCompanies = async () => {
      setLoading(true);
      try {
        // Build filter only with non-empty values
        const filter: Record<string, any> = {};
        if (filterParams.legalName) {
          filter.legalName = filterParams.legalName;
        }
        const refTrimmed = debouncedReferenceIdSearch.trim();
        if (refTrimmed) {
          filter.gtid = refTrimmed;
        }
        if (filterParams.status) {
          filter.status = filterParams.status;
        }
        if (filterParams.industryCategory) {
          filter.industryCategory = filterParams.industryCategory;
        }
        if (filterParams.subIndustry && (filterParams.subIndustry as string[]).length > 0) {
          filter.subIndustry = filterParams.subIndustry;
        }
        if (filterParams.collaborationIntent && (filterParams.collaborationIntent as string[]).length > 0) {
          filter.collaborationIntent = filterParams.collaborationIntent;
        }
        if (filterParams.operatingCountries && (filterParams.operatingCountries as string[]).length > 0) {
          filter.operatingCountries = filterParams.operatingCountries;
        }
        if (
          filterParams.preferredCollaborationRegions &&
          (filterParams.preferredCollaborationRegions as string[]).length > 0
        ) {
          filter.preferredCollaborationRegions = filterParams.preferredCollaborationRegions;
        }

        const res = await trpc.admin.listAll.query({
          page: params.page,
          limit: params.limit,
          filter: Object.keys(filter).length > 0 ? filter : undefined,
          sorting: params.sorting.length > 0 ? params.sorting : undefined,
        });

        // Map response to match frontend types
        setCompanies(
          res.companies.map((c: any) => ({
            id: c.id || c.gtid, // Use id if available, fallback to gtid
            legalName: c.name,
            gtid: c.gtid,
            status: c.verificationStatus,
            industryCategory: c.industryCategory?.length > 0 ? c.industryCategory : null,
            subIndustry: c.subIndustry || [],
            collaborationIntent: c.collaborationIntent || [],
            operatingCountries: c.operatingCountries || [],
            preferredCollaborationRegions: c.preferredCollaborationRegions || [],
            lastSeenAt: c.lastSeenAt || null,
            viewCount: c.viewCount ?? 0,
            creditBalance: c.creditBalance ?? 0,
          }))
        );
        setTotalRows(res.meta.total);
      } catch (err) {
        handleTrpcError(err);
        setCompanies([]);
        setTotalRows(0);
      } finally {
        setLoading(false);
      }
    };

    fetchCompanies();
  }, [pagination.pageIndex, pagination.pageSize, filterParams, sorting, debouncedReferenceIdSearch]);

  useEffect(() => {
    if (pagination.pageIndex !== 0) {
      setPagination((prev) => ({ ...prev, pageIndex: 0 }));
    }
  }, [filterParams, debouncedReferenceIdSearch]);

  const columns: MRT_ColumnDef<CompanyListItem>[] = useMemo(
    () => [
      {
        accessorKey: "legalName",
        header: "Company Name",
        size: 200,
        Cell: ({ cell }) => {
          const value = cell.getValue() as string | null;
          return value || "—";
        },
      },
      {
        accessorKey: "gtid",
        header: "Reference ID",
        size: 150,
        enableColumnFilter: false,
      },
      {
        accessorKey: "status",
        header: "Verification Status",
        size: 160,
        filterVariant: "select",
        mantineFilterSelectProps: {
          data: Object.entries(VERIFICATION_STATUS_LABELS).map(([value, label]) => ({
            value,
            label,
          })),
          clearable: true,
        },
        Cell: ({ cell }) => {
          const value = cell.getValue() as string;
          return <VerificationStatusBadge status={value} />;
        },
      },
      {
        accessorKey: "lastSeenAt",
        id: "lastSeenAt",
        header: "Last Seen",
        size: 160,
        enableColumnFilter: false,
        enableSorting: true,
        Cell: ({ cell }) => {
          const value = cell.getValue() as string | null;
          if (!value) return <span style={{ color: "#94a3b8" }}>Never</span>;
          const date = new Date(value);
          const now = new Date();
          const diffMs = now.getTime() - date.getTime();
          const diffMins = Math.floor(diffMs / 60000);
          const diffHours = Math.floor(diffMs / 3600000);
          const diffDays = Math.floor(diffMs / 86400000);

          let label: string;
          if (diffMins < 1) label = "Just now";
          else if (diffMins < 60) label = `${diffMins}m ago`;
          else if (diffHours < 24) label = `${diffHours}h ${diffMins % 60}m ago`;
          else if (diffDays < 7) label = `${diffDays}d ${diffHours % 24}h ago`;
          else label = date.toLocaleDateString();

          return (
            <Tooltip label={date.toLocaleString()}>
              <span style={{ color: diffMins < 5 ? "#16a34a" : "#64748b" }}>{label}</span>
            </Tooltip>
          );
        },
      },
      {
        accessorKey: "viewCount",
        header: "View Count",
        size: 120,
        enableColumnFilter: false,
        enableSorting: true,
        Cell: ({ cell }) => {
          const value = cell.getValue() as number;
          return value ?? 0;
        },
      },
      {
        accessorKey: "creditBalance",
        header: "Credit Balance",
        size: 140,
        enableColumnFilter: false,
        enableSorting: false,
        Cell: ({ cell }) => {
          const value = cell.getValue() as number;
          return <span style={{ fontWeight: 500 }}>{value}</span>;
        },
      },
      {
        accessorKey: "industryCategory",
        header: "Industry Category",
        size: 220,
        filterVariant: "multi-select",
        mantineFilterMultiSelectProps: {
          data: Object.entries(OPERATING_INDUSTRY_LABELS).map(([value, label]) => ({
            value,
            label,
          })),
          clearable: true,
        },
        Cell: ({ cell }) => {
          const value = cell.getValue() as string[] | null;
          if (!value || value.length === 0) return <span style={{ color: "#94a3b8" }}>—</span>;
          return value.map((v) => getIndustryLabel(v)).join(", ");
        },
      },
      {
        accessorKey: "industryCategory",
        header: "Industry Category",
        size: 220,
        filterVariant: "multi-select",
        mantineFilterMultiSelectProps: {
          data: Object.entries(OPERATING_INDUSTRY_LABELS).map(([value, label]) => ({
            value,
            label,
          })),
          clearable: true,
        },
        Cell: ({ cell }) => {
          const value = cell.getValue() as string[] | null;
          if (!value || value.length === 0) return <span style={{ color: "#94a3b8" }}>—</span>;
          return value.map((v) => getIndustryLabel(v)).join(", ");
        },
      },
      {
        accessorKey: "subIndustry",
        header: "Sub Industry",
        size: 200,
        filterVariant: "multi-select",
        mantineFilterMultiSelectProps: {
          data: Object.entries(SUB_INDUSTRY_LABELS).map(([value, label]) => ({
            value,
            label,
          })),
          clearable: true,
          searchable: true,
        },
        enableSorting: false,
        Cell: ({ cell }) => {
          const values = cell.getValue() as string[];
          if (!values || values.length === 0) return <span style={{ color: "#94a3b8" }}>—</span>;
          return values.map(getSubIndustryLabel).join(", ");
        },
      },
      {
        accessorKey: "collaborationIntent",
        header: "Collaboration Intent",
        size: 200,
        filterVariant: "multi-select",
        mantineFilterMultiSelectProps: {
          data: Object.entries(COLLABORATION_INTENT_LABELS).map(([value, label]) => ({
            value,
            label,
          })),
          clearable: true,
          searchable: true,
        },
        enableSorting: false,
        Cell: ({ cell }) => {
          const values = cell.getValue() as string[];
          if (!values || values.length === 0) return <span style={{ color: "#94a3b8" }}>—</span>;
          return values.map(getCollaborationIntentLabel).join(", ");
        },
      },
      {
        accessorKey: "operatingCountries",
        header: "Operating Countries",
        size: 200,
        filterVariant: "multi-select",
        mantineFilterMultiSelectProps: {
          data: COUNTRIES_FILTER_DATA,
          clearable: true,
          searchable: true,
        },
        enableSorting: false,
        Cell: ({ cell }) => {
          const values = cell.getValue() as string[];
          if (!values || values.length === 0) return <span style={{ color: "#94a3b8" }}>—</span>;
          const shown = values.map((v) => displayHeadquartersText(v)).filter((v) => v !== "N/A");
          if (shown.length === 0) return <span style={{ color: "#94a3b8" }}>—</span>;
          return shown.join(", ");
        },
      },
      {
        accessorKey: "preferredCollaborationRegions",
        header: "Preferred Regions",
        size: 200,
        filterVariant: "multi-select",
        mantineFilterMultiSelectProps: {
          data: REGIONS_FILTER_DATA,
          clearable: true,
          searchable: true,
        },
        enableSorting: false,
        Cell: ({ cell }) => {
          const values = cell.getValue() as string[];
          if (!values || values.length === 0) return <span style={{ color: "#94a3b8" }}>—</span>;
          return values
            .map((id) => {
              const region = regionsData.regions.find((r) => r.id === id);
              return region?.name || id;
            })
            .join(", ");
        },
      },
    ],
    []
  );

  if (loading && companies.length === 0) {
    return (
      <Center h="50vh">
        <Loader size="lg" variant="dots" />
      </Center>
    );
  }

  return (
    <Flex direction="column" w="100%" p="xl">
      <Title order={2} fw={400} mb="md">
        Companies
      </Title>

      <TextInput
        placeholder="Search by reference ID (GTid)"
        leftSection={<IconSearch size={16} />}
        value={referenceIdSearch}
        onChange={(e) => setReferenceIdSearch(e.currentTarget.value)}
        maw={400}
        mb="lg"
      />

      <MantineReactTable
        columns={columns}
        data={companies}
        enableRowNumbers
        enableStickyHeader
        enableColumnPinning
        enableRowActions
        enableColumnResizing
        columnResizeMode="onEnd"
        layoutMode="semantic"
        manualPagination
        manualFiltering
        manualSorting
        enableGlobalFilter={false}
        rowCount={totalRows}
        onPaginationChange={setPagination}
        onColumnFiltersChange={setColumnFilters}
        onSortingChange={setSorting}
        state={{
          pagination,
          columnFilters,
          sorting,
          isLoading: loading,
          columnVisibility,
        }}
        positionActionsColumn="last"
        renderRowActions={({ row }) => (
          <Group gap={4} wrap="nowrap">
            <Tooltip label="Copy discover URL">
              <ActionIcon
                c="#475569"
                variant="transparent"
                onClick={() => copyDiscoverUrl(row.original.id)}
              >
                <IconLink size={18} />
              </ActionIcon>
            </Tooltip>
            <Tooltip label="View Details">
              <ActionIcon
                c="#475569"
                variant="transparent"
                onClick={() => navigate(`/admin/companies/${row.original.id}`)}
              >
                <IconEye />
              </ActionIcon>
            </Tooltip>
          </Group>
        )}
        onColumnVisibilityChange={setColumnVisibility}
        initialState={{
          columnPinning: { right: ["mrt-row-actions"] },
          density: "md",
        }}
        // initialState={{
        //   columnPinning: { right: ["mrt-row-actions"] },
        //   density: "md",
        //   columnVisibility: {
        //     industryCategory: false,
        //     subIndustry: false,
        //     collaborationIntent: false,
        //     operatingCountries: false,
        //     preferredCollaborationRegions: false,
        //   },
        // }}
        mantineTableBodyCellProps={{
          style: {
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            maxWidth: 200,
          },
        }}
        mantineTableHeadCellProps={{
          style: {
            fontWeight: 500,
            fontSize: "var(--mantine-font-size-md)",
            color: theme.colors.slate[6],
          },
        }}
        mantineTableContainerProps={{
          style: {
            maxHeight: "calc(100vh - 250px)",
            minHeight: "100px",
            width: "100%",
            overflowX: "auto",
          },
        }}
        renderEmptyRowsFallback={() => (
          <tr>
            <td colSpan={columns.length + 2} style={{ textAlign: "center", padding: "2rem" }}>
              No companies found. Try adjusting your filters.
            </td>
          </tr>
        )}
      />
    </Flex>
  );
}
